import {
  HIGHLIGHT_COLORS,
  useDataSourceGroups,
  useHighlightedTags,
} from "../singleTimelineStore";
import { useTagList } from "components/FlightTrends/TrendsFilters/useTagList";
import {
  IconButton,
  Button,
  MenuItem,
  Menu,
  Divider,
  Tooltip,
  Checkbox,
} from "@mui/material";
import type { TagDetails } from "types/types";
import { useState } from "react";
import { PNL_COLORS, useBreakpoints } from "constants/constants";
import Close from "@mui/icons-material/Close";
import { highlightTags } from "components/Dashboard/PnlSummaryChart/pnlSummaryChartUtils";
import {
  useIsAdmin,
  useIsSingleProcessPage,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useVisibleSegmentsMetadata } from "../useVisibleSegmentsMetadata";
import { PnlCategoriesEnum } from "types/enums";
import { camelCase, startCase } from "lodash";
import {
  HighlightedTagIndicatorStyles,
  HighlightTagsSelectorStyles,
  MenuItemsWithTitleStyles,
  StyledColoredDiamond,
  StyledColoredSquare,
} from "./HighlightTagsSelectorStyles";
import { useHighlightOverlappingSegments, useIsDarkMode } from "store/atoms";
import {
  darkModeModalBackgroundColor,
  darkModeTextColor,
} from "constants/colors";
import { useSelector } from "store/useSelector";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";

/** a "+ Tag Highlight" dropdown to render below a Timeline which allows selecting tags to highlight, and displays current selected tags */
export function HighlightTagsSelector() {
  const [highlightedTags] = useHighlightedTags();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  const { selectedDataSourceGroups, setSelectedDataSourceGroups } =
    useDataSourceGroups();

  const areAnyTagsHighlighted =
    [...highlightedTags, ...selectedDataSourceGroups].length > 0;
  const isStorybook = useIsStorybook();
  const isAdminUser = useIsAdmin();
  const [highlightOverlappingSegments, setHighlightOverlappingSegments] =
    useHighlightOverlappingSegments();
  const { flightEnableAlternateTimeline } = useFlagsWithDefaults();
  const { isDesktopXlOrLarger } = useBreakpoints();
  return (
    <HighlightTagsSelectorStyles $isDarkMode={isDarkMode}>
      <div className="highlightedTagsChips">
        {highlightedTags.map(({ name, pnlCategory, highlightColor }) => (
          <HighlightedTagIndicator key={name} {...{ name, highlightColor }} />
        ))}
        {selectedDataSourceGroups.map(({ name, id }) => (
          <HighlightedTagIndicator
            key={name}
            {...{ name: `🕵️‍♀️ ${id} - ${name}`, highlightColor: "tomato" }}
            handleRemove={() => {
              setSelectedDataSourceGroups(prev =>
                prev.filter(prevGroup => prevGroup.name !== name)
              );
            }}
          />
        ))}
        {highlightOverlappingSegments && (
          <HighlightedTagIndicator
            name="🕵️‍♀️ Highlight Overlapping Segments"
            highlightColor="tomato"
            handleRemove={() => {
              setHighlightOverlappingSegments(false);
            }}
          />
        )}
      </div>
      <Button
        disabled={highlightedTags.length >= HIGHLIGHT_COLORS.length}
        aria-controls="highlight-tags-menu"
        aria-haspopup="true"
        className="btnTagHighlight"
        onClick={handleClick}
      >
        {areAnyTagsHighlighted
          ? `+ ${translate("Add")}`
          : `+ ${translate("Tag_Highlight")}`}
      </Button>
      <Menu
        id="highlight-tags-menu"
        anchorEl={anchorEl}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={"highlightTagsPaper"}
        disableScrollLock={true}
        {...(isDarkMode
          ? {
              PaperProps: {
                style: {
                  background: darkModeModalBackgroundColor,
                  color: darkModeTextColor,
                },
              },
            }
          : {})}
        slotProps={{
          paper: {
            style: isDesktopXlOrLarger
              ? { left: "calc(50vw - 50%) !important" }
              : {},
          },
        }}
        variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
      >
        <AllTagsMenuItems {...{ handleClose }} />
        <TagsMetadataMenuItems {...{ handleClose }} />
        {(isAdminUser || flightEnableAlternateTimeline) && (
          <AlternateTimelinesMenuItems />
        )}
        {(isAdminUser || isStorybook) && (
          <HighlightOverlappingSegmentsCheckbox />
        )}
      </Menu>
    </HighlightTagsSelectorStyles>
  );
}

function AllTagsMenuItems({ handleClose }) {
  const [highlightedTags, setHighlightedTags] = useHighlightedTags();
  const isSingleProcessPage = useIsSingleProcessPage();
  const allTags: TagDetails[] = useTagList({
    shouldIncludePnlCategories: false,
    singleTimelineOnly: isSingleProcessPage,
  });
  return (
    <>
      {allTags.map(({ name, pnlCategory }) => (
        <MenuItem
          key={name}
          value={name}
          disabled={Boolean(highlightedTags.find(tag => tag.name === name))}
          style={{ padding: "8px 16px" }}
          onClick={() => {
            setHighlightedTags(prev => [
              ...prev,
              {
                name,
                pnlCategory,
                highlightColor: HIGHLIGHT_COLORS[prev.length],
              },
            ]);
            handleClose();
          }}
        >
          <StyledColoredSquare
            style={{ background: PNL_COLORS[pnlCategory] }}
          />
          {name}
        </MenuItem>
      ))}
    </>
  );
}

/** indicates a tag which is highlighted, plus a button to un-highlight it */
function HighlightedTagIndicator({
  name,
  highlightColor,
  handleRemove,
}: {
  name: string;
  highlightColor: string;
  handleRemove?: () => void;
}) {
  const [, setHighlightedTags] = useHighlightedTags();
  function handleRemoveThisTag() {
    if (handleRemove) {
      handleRemove();
      return;
    }
    unhighlightAssociatedSegments();
    setHighlightedTags(prev =>
      prev
        .filter(tag => tag.name !== name)
        // reset all colors to match HIGHLIGHT_COLORS orders
        .map((tag, idx) => ({ ...tag, highlightColor: HIGHLIGHT_COLORS[idx] }))
    );
  }

  const visibleRange = useSelector(s => s.legacy.visibleRange);

  const highlightAssociatedSegments = () => {
    highlightTags({
      tag: camelCase(name),
      doHighlight: true,
      isOverall: false,
      rangeToHighlight: visibleRange,
    });
  };
  const unhighlightAssociatedSegments = () =>
    highlightTags({
      tag: camelCase(name),
      doHighlight: false,
      isOverall: false,
      rangeToHighlight: visibleRange,
    });
  const [isDarkMode] = useIsDarkMode();
  return (
    <HighlightedTagIndicatorStyles
      onMouseEnter={highlightAssociatedSegments}
      onMouseLeave={unhighlightAssociatedSegments}
      $isDarkMode={isDarkMode}
    >
      <IconButton onClick={handleRemoveThisTag} size="large">
        <Close />
      </IconButton>
      <div className="colorIndicator" style={{ background: highlightColor }} />
      <div className={`highlighted-tag-text ${camelCase(name)}`}>{name}</div>
    </HighlightedTagIndicatorStyles>
  );
}

function TagsMetadataMenuItems({ handleClose }) {
  const [highlightedTags, setHighlightedTags] = useHighlightedTags();

  const allMetadata = useVisibleSegmentsMetadata();
  const isAdminUser = useIsAdmin();
  const [isDarkMode] = useIsDarkMode();
  return (
    <>
      {Object.entries(allMetadata).map(([metaTitle, metaValues]) => {
        // only show traceability for internal users
        const isTraceability = metaTitle === "traceability";
        if ((isTraceability && !isAdminUser) || metaValues.length === 0) {
          return null;
        }
        return (
          <MenuItemsWithTitleStyles key={metaTitle} $isDarkMode={isDarkMode}>
            <Divider />
            <Tooltip
              title={isTraceability ? "only visible to @raven users" : ""}
            >
              <div className="sectionTitle">{startCase(metaTitle)}</div>
            </Tooltip>
            {metaValues.map((metaValue, idx) => (
              <MenuItem
                key={metaValue}
                value={metaValue}
                disabled={Boolean(
                  highlightedTags.find(tag => tag.name === metaValue)
                )}
                onClick={() => {
                  setHighlightedTags(prev => [
                    ...prev,
                    {
                      name: metaValue,
                      // pnlCategory is unused in metadata menu items (in normal tag menu items it determines the circle colour)
                      pnlCategory: PnlCategoriesEnum.uncollected,
                      highlightColor: HIGHLIGHT_COLORS[prev.length],
                    },
                  ]);
                  handleClose();
                }}
              >
                <StyledColoredDiamond
                  style={{ background: HIGHLIGHT_COLORS[idx] }}
                />
                {metaValue}
              </MenuItem>
            ))}
          </MenuItemsWithTitleStyles>
        );
      })}
    </>
  );
}

function AlternateTimelinesMenuItems() {
  const {
    dataSourceGroups,
    selectedDataSourceGroups,
    setSelectedDataSourceGroups,
  } = useDataSourceGroups();
  const [isDarkMode] = useIsDarkMode();

  const isAdminUser = useIsAdmin();
  const { flightEnableAlternateTimeline } = useFlagsWithDefaults();
  const minimal = !isAdminUser && flightEnableAlternateTimeline; // for non-raven users, only show the retroactive data source group

  return (
    <>
      <MenuItemsWithTitleStyles $isDarkMode={isDarkMode}>
        <Divider />
        <Tooltip title={"only visible to @raven users"}>
          <div className="sectionTitle">
            Alternate Timeline{minimal ? "" : ": Data Source Groups"}
          </div>
        </Tooltip>
        {[...dataSourceGroups]
          .sort((a, b) => Number(a.id) - Number(b.id))
          .filter(group => {
            return minimal ? group.name === "Retroactive" : true;
          })
          .map(group => {
            const checked = Boolean(
              selectedDataSourceGroups.find(g => g.name === group.name)
            );
            return (
              <MenuItem
                key={group.name}
                value={group.name}
                onClick={() => {
                  if (checked) {
                    setSelectedDataSourceGroups(prev =>
                      prev.filter(g => g.name !== group.name)
                    );
                  } else {
                    setSelectedDataSourceGroups(prev => [...prev, group]);
                  }
                }}
              >
                <Checkbox checked={checked} />
                🕵️‍♀️ {minimal ? " " : group.id + " - "}
                {group.name}
              </MenuItem>
            );
          })}
      </MenuItemsWithTitleStyles>
    </>
  );
}

function HighlightOverlappingSegmentsCheckbox() {
  const [isDarkMode] = useIsDarkMode();
  const [checked, setChecked] = useHighlightOverlappingSegments();
  return (
    <MenuItemsWithTitleStyles $isDarkMode={isDarkMode}>
      <Divider />
      <Tooltip title={"only visible to @raven users"}>
        <div className="sectionTitle">Cool Tools</div>
      </Tooltip>
      <MenuItem
        onClick={() => {
          setChecked(prev => !prev);
        }}
      >
        <Checkbox checked={checked} />
        🕵️‍♀️ Highlight Segment Overlaps
      </MenuItem>
    </MenuItemsWithTitleStyles>
  );
}
